import * as React from 'react';
import { createUseStyles } from 'react-jss';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';

import { useUserPermissionsUIStore } from '@core/useStores';
import { NavLink } from '@shared/components/NavLink';
import { ThemeProps } from '@styles/theme';
import { messages } from '../../header.messages';
import { useTheme } from '@mui/material/styles';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: ThemeProps) => ({
    vertical: {},
    compact: {},
    menuWrapper: {
        listStyle: 'none',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: theme.spacing(1.5),
        padding: 0,
        margin: 0,
        minWidth: 0,

        '& li': {
            flexShrink: 0,
        },

        '& li:last-child': {
            marginRight: 0,
        },

        '&$compact': {
            gap: theme.spacing(1),
        },

        '&$compact li': {
            display: 'flex',
        },

        '&$vertical': {
            flexDirection: 'column',
            alignItems: 'stretch',
            flexWrap: 'nowrap',
            gap: 0,
            width: '100%',

            '& li': {
                width: '100%',
                marginBottom: theme.spacing(2),
            },

            '& li > a': {
                width: '100%',
                justifyContent: 'flex-start',
            },
        },
    },
}))

type MainMenuProps = {
    vertical?: boolean;
    onNavigate?: () => void;
    compact?: boolean;
}

type MenuItem = {
    key: string;
    to: string;
    label: string;
    icon: React.ReactNode;
    isVisible: boolean;
};

export const MainMenu = ({ vertical = false, onNavigate, compact = false }: MainMenuProps) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const userPermissionsUIStore = useUserPermissionsUIStore();

    const menuItems: MenuItem[] = [
        {
            key: 'companies',
            to: '/companies',
            label: messages['mainMenu.companies'],
            icon: <BusinessOutlinedIcon />,
            isVisible: userPermissionsUIStore.isCompaniesAvailable,
        },
        {
            key: 'users',
            to: '/users',
            label: messages['mainMenu.users'],
            icon: <PeopleOutlineOutlinedIcon />,
            isVisible: userPermissionsUIStore.isUsersAvailable,
        },
        {
            key: 'groups',
            to: '/groups',
            label: messages['mainMenu.groups'],
            icon: <GroupsOutlinedIcon />,
            isVisible: userPermissionsUIStore.isGroupsAvailable,
        },
        {
            key: 'procedures',
            to: '/procedures',
            label: messages['mainMenu.procedures'],
            icon: <AssignmentOutlinedIcon />,
            isVisible: userPermissionsUIStore.isProceduresAvailable,
        },
        {
            key: 'collaboration',
            to: '/collaboration',
            label: messages['mainMenu.collaboration'],
            icon: <HubOutlinedIcon />,
            isVisible: userPermissionsUIStore.isCollaborationsAvailable,
        },
        {
            key: 'analytics',
            to: '/analytics',
            label: messages['mainMenu.analytics'],
            icon: <QueryStatsOutlinedIcon />,
            isVisible: userPermissionsUIStore.isAnalyticsAvailable,
        },
        {
            key: 'licenses',
            to: '/licenses',
            label: messages['mainMenu.licenses'],
            icon: <KeyOutlinedIcon />,
            isVisible: userPermissionsUIStore.isLicensesAvailable,
        },
        {
            key: 'download',
            to: '/resources',
            label: messages['mainMenu.download'],
            icon: <LibraryBooksOutlinedIcon />,
            isVisible: userPermissionsUIStore.isDownloadAvailable,
        },
    ];

    return (
        <nav>
            <ul className={classNames(styles.menuWrapper, {
                [styles.vertical]: vertical,
                [styles.compact]: compact && !vertical,
            })}>
                {menuItems.filter(({ isVisible }) => isVisible).map(({ key, to, label, icon }) => (
                    <li key={key}>
                        <NavLink
                            to={to}
                            onClick={onNavigate}
                            isText={!compact || vertical}
                            title={compact && !vertical ? label : undefined}
                            ariaLabel={label}
                            testId={`menu-item-${key}`}
                        >
                            {compact && !vertical ? icon : label}
                        </NavLink>
                    </li>
                ))}
            </ul>
        </nav>
    );
}
